import { createFileRoute, Link } from "@tanstack/react-router";
import { Users, Luggage, Check, ArrowRight, Clock, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FLEET } from "@/lib/fleet";
import { useFormatPrice } from "@/lib/currency";

export const Route = createFileRoute("/fleet")({
  head: () => ({
    meta: [
      { title: "Our Fleet — Vehicles for Morocco Private Transfers | Bacha Way" },
      { name: "description", content: "Six vehicle categories from Economy sedan to Luxury van for private chauffeured transfers across Morocco. Fixed prices, free waiting time, free cancellation." },
      { property: "og:title", content: "Our Fleet — Bacha Way Travel" },
      { property: "og:description", content: "Six vehicle categories from Economy to Luxury — private chauffeured transfers across Morocco." },
      { property: "og:url", content: "https://bachawaytravel.com/fleet" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/fleet" }],
  }),
  component: FleetPage,
});

function FleetPage() {
  const fmt = useFormatPrice();
  const { t } = useTranslation();
  const PERKS = [
    { icon: Clock, label: t("fleet.perk1") },
    { icon: Check, label: t("fleet.perk2") },
    { icon: Shield, label: t("fleet.perk3") },
  ];

  return (
    <div className="bg-secondary/30 min-h-screen">
      <div className="mx-auto max-w-5xl px-4 py-12 md:py-20 md:px-6">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("fleet.eyebrow")}</div>
          <h1 className="mt-3 font-display text-4xl md:text-6xl text-foreground">
            Our Fleet of Private Transfer Vehicles in Morocco
          </h1>
          <p className="mt-4 mx-auto max-w-2xl text-muted-foreground">
            {t("fleet.lead")}
          </p>
          <h2 className="sr-only">VIP transfer Morocco — private driver Agadir, Marrakech airport transfer & luxury VTC Taghazout</h2>
        </div>

        {/* Perks strip */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PERKS.map((p) => (
            <div
              key={p.label}
              className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-border shadow-sm"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <p.icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium text-foreground">{p.label}</span>
            </div>
          ))}
        </div>

        {/* Vehicle cards */}
        <div className="mt-10 space-y-4">
          {FLEET.map((v) => (
            <article
              key={v.id}
              className="group rounded-2xl bg-white ring-1 ring-border shadow-sm transition-all hover:shadow-xl hover:ring-primary/30 overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row sm:items-stretch">
                <div className="flex items-center justify-center px-6 py-6 sm:w-80 sm:flex-shrink-0">
                  <img
                    src={v.image}
                    alt={`${v.name} class private transfer vehicle — ${v.type}`}
                    loading="lazy"
                    width={1024}
                    height={640}
                    className="h-48 sm:h-56 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="flex-1 px-5 pb-5 pt-2 sm:py-5 sm:pl-2 sm:pr-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-foreground">{v.name} — luxury VTC Morocco</h3>
                      <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="h-4 w-4" /> {v.pax}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Luggage className="h-4 w-4" /> {v.bags}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t("fleet.from")}</div>
                      <div className="text-xl sm:text-2xl font-bold text-foreground">
                        {fmt(v.base)}
                      </div>
                    </div>
                  </div>

                  <p className="mt-3 text-sm text-muted-foreground">{v.description}</p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                      <Clock className="h-3 w-3" /> {t("fleet.perk1")}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                      <Check className="h-3 w-3" /> {t("fleet.perk2")}
                    </span>
                  </div>

                  <div className="mt-5 flex justify-end">
                    <Link
                      to="/booking"
                      className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
                    >
                      {t("transfer.bookThis")} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 rounded-3xl bg-gradient-to-br from-primary to-[oklch(0.22_0.15_268)] p-10 text-center text-white shadow-[0_30px_60px_-30px_rgba(30,42,120,0.6)]">
          <h2 className="font-display text-3xl md:text-4xl">{t("fleet.ctaTitle")}</h2>
          <p className="mt-3 text-white/80 max-w-md mx-auto text-sm">
            {t("fleet.ctaLead")}
          </p>
          <Link
            to="/booking"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary transition-transform hover:scale-[1.02]"
          >
            {t("common.bookCta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
